/**
 * PathFunction
 * @description A function that takes an id and returns a path
 */
type PathFunction = (id: number | string) => string;

/**
 * PathObjDef
 * @description A type that defines a path object
 */
type PathObjDef = {
  [pathName: string]: string | PathFunction;
};

/**
 * Paths
 * @description Define all paths in the application \
 * Currently, only some paths are defined. \
 * In the future, all paths should be defined here. \
 * Let's move all paths from the components to here gradually.
 */
type Paths = {
  // [pathName: string]: string;
  [pathName: string]: string | PathFunction | PathObjDef;
};

/**
 * paths: Paths
 * @description Define all paths in the application
 * Until the total number of paths is small, it should be defined at flat level.
 * However, if the total number of paths is large, it should be defined at nested level.
 * @example
 * const paths: Paths = {
 *  // Root level paths
 *  ContractManagement: "/contract-management",
 *  PlanCancelPreProcess: "/plan-cancel-preprocess",
 *  PlanCancel: "/plan-cancel",
 *  PlanCancelCompleted: "/plan-cancel-completed",
 *  NotFound: "/404",
 *  // Nested level paths
 *  Bizsol: {
 *    Callback: "/bizsol/callback",
 *    RedirectLogin: "/bizsol/redirect-login",
 *    ...
 *  },
 *  ConnectBiz: {
 *    Callback: "/connect-biz/callback",
 *    RedirectLogin: "/connect-biz/redirect-login",
 *    ...
 *  },
 *  Plan: {
 *    Select: "/plan/select-plan",
 *    CompleteSelect: "/plan/complete-select-plan",
 *    Change: "/plan/change-plan",
 *  }
 * ...
 * };
 */
const paths = {
  ContractManagement: "/contract-management",
  PlanCancelPreProcess: "/plan-cancel-preprocess",
  PlanCancel: "/plan-cancel",
  PlanCancelCompleted: "/plan-cancel-completed",
};

/**
 * Check if the object satisfies the Interface
 * @param obj - The object to be checked
 * @returns The object that satisfies the Interface
 */
export function enforceInterface<T>(obj: T): T {
  return obj;
}

/**
 * Check if the paths object satisfies the Paths Interface
 * This invocation is necessary to ensure that the paths object satisfies the Paths Interface
 * while compiling the TypeScript code.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const checkPathsType = enforceInterface<Paths>(paths);

export default paths;
